import React, { useEffect, useRef, useState } from "react";
import { RotateCcw, Save, Images, X, Play, Lock, ChevronRight, Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const BG_GRADIENTS = ["from-[#0b1e3a] via-[#1b2a4a] to-[#3a1e4a]", "from-[#2a0b1e] via-[#4a1b2a] to-[#1e2a4a]", "from-[#0b2a24] via-[#1b4a3a] to-[#2a1e4a]"];

export function VNPlayer({ scene, code = "", canSave = false, className = "" }) {
  const nodes = scene.nodes || {};
  const cast = scene.cast || [];
  const bgUrls = scene.bg_urls || {};
  const bgKeys = Object.keys(scene.backgrounds || {});

  const [nodeId, setNodeId] = useState(scene.start);
  const [lineIdx, setLineIdx] = useState(0);
  const [seen, setSeen] = useState([]);
  const [slots, setSlots] = useState([]);
  const [panel, setPanel] = useState(null); // 'saves' | 'gallery'
  const [progLoaded, setProgLoaded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [voiceOn, setVoiceOn] = useState(!!scene.voiced);
  const [showEnd, setShowEnd] = useState(false);
  const seenPosted = useRef(new Set());
  const musicRef = useRef(null);
  const voiceRef = useRef(null);
  const interacted = useRef(false);

  const node = nodes[nodeId] || {};
  const lines = node.lines || [];
  const line = lines[lineIdx] || null;
  const atLineEnd = lineIdx >= lines.length - 1;
  const speaker = line ? cast.find((c) => c.name === line.speaker) : null;
  const isNarrator = !line || line.speaker === "Narrator" || !speaker;
  const spriteUrl = !isNarrator ? asset((speaker.sprites || {})[line.emotion] || (speaker.sprites || {}).neutral || speaker.image) : null;
  const bg = asset(bgUrls[node.bg]);
  const grad = BG_GRADIENTS[Math.abs((node.bg || "").length) % BG_GRADIENTS.length];
  const showChoices = atLineEnd && (node.choices || []).length > 0 && !node.ending;
  const showEnding = node.ending && showEnd;

  useEffect(() => {
    if (canSave && code) {
      setProgLoaded(false);
      api.get(`/vn/play/${code}/progress`)
        .then(({ data }) => { setSlots(data.slots || []); setSeen((s) => Array.from(new Set([...s, ...(data.seen_bg || [])]))); })
        .catch(() => {})
        .finally(() => setProgLoaded(true));
    } else { setProgLoaded(true); }
  }, [canSave, code]);

  useEffect(() => {
    const b = node.bg;
    if (b) setSeen((s) => (s.includes(b) ? s : [...s, b]));
    if (b && canSave && code && !seenPosted.current.has(b)) {
      seenPosted.current.add(b);
      api.post(`/vn/play/${code}/seen`, { bg_id: b }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [nodeId]);

  useEffect(() => {
    const v = voiceRef.current;
    if (v) { try { v.pause(); } catch { /* ignore */ } }
    const u = line?.voiceUrl;
    if (voiceOn && u && interacted.current && v) {
      v.src = asset(u); v.currentTime = 0; v.play().catch(() => {});
    }
    // eslint-disable-next-line
  }, [nodeId, lineIdx, voiceOn]);

  const kick = () => {
    interacted.current = true;
    const m = musicRef.current;
    if (m && !muted && scene.music) m.play().catch(() => {});
  };
  const goNode = (id) => { kick(); setShowEnd(false); if (nodes[id]) { setNodeId(id); setLineIdx(0); } };
  const advance = () => {
    kick();
    if (showEnding || showChoices) return;
    if (lineIdx < lines.length - 1) { setLineIdx((i) => i + 1); return; }
    if (node.ending) { setShowEnd(true); return; }
    if ((node.choices || []).length) return;
    if (node.next) goNode(node.next);
  };
  const restart = () => { setNodeId(scene.start); setLineIdx(0); setPanel(null); setShowEnd(false); };

  const saveSlot = async (slot) => {
    if (!canSave || !code) { toast.error("Log in and open the shared scene to save."); return; }
    try {
      const label = node.ending_title || (line ? line.text.slice(0, 40) : "Saved moment");
      const { data } = await api.post(`/vn/play/${code}/save`, { slot, node_id: nodeId, seen_bg: node.bg, label });
      setSlots(data.slots || []);
      toast.success(`Saved to slot ${slot + 1} ✨`);
    } catch { toast.error("Couldn't save that slot."); }
  };
  const loadSlot = (sl) => { goNode(sl.node_id); setPanel(null); toast.success(`Loaded slot ${sl.slot + 1}`); };
  const slotAt = (i) => slots.find((s) => s.slot === i);

  return (
    <div className={`relative w-full h-full rounded-2xl overflow-hidden select-none bg-black ${className}`} data-testid="vn-player">
      {scene.music && <audio ref={musicRef} src={asset(scene.music.url)} loop preload="auto" />}
      <audio ref={voiceRef} preload="auto" />
      {/* Background */}
      {bg ? (
        <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="vn-bg" />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${grad}`} data-testid="vn-bg-fallback" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />

      {/* Advance layer */}
      <button onClick={advance} data-testid="vn-advance" aria-label="Continue"
        className="absolute inset-0 z-10 cursor-pointer" style={{ background: "transparent" }} />

      {/* Character sprite */}
      {spriteUrl && (
        <img key={`${nodeId}-${lineIdx}`} src={spriteUrl} alt={speaker.name}
          data-testid="vn-sprite"
          className="absolute bottom-[24%] left-1/2 -translate-x-1/2 h-[62%] max-h-[560px] object-contain z-20 drop-shadow-2xl vn-fade pointer-events-none" />
      )}

      {/* Top controls */}
      <div className="absolute top-3 right-3 z-40 flex items-center gap-2">
        {scene.music && (
          <button onClick={() => { const m = !muted; setMuted(m); const el = musicRef.current; if (el) { if (m) { el.pause(); } else { interacted.current = true; el.play().catch(() => {}); } } }}
            data-testid="vn-music-toggle" title={muted ? "Music off" : "Music on"}
            className="w-9 h-9 grid place-items-center rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
        {scene.voiced && (
          <button onClick={() => setVoiceOn((v) => !v)} data-testid="vn-voice-toggle" title={voiceOn ? "Voices on" : "Voices off"}
            className="w-9 h-9 grid place-items-center rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70">
            {voiceOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
          </button>
        )}
        <button onClick={() => setPanel(panel === "gallery" ? null : "gallery")} data-testid="vn-gallery-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white hover:bg-black/70">
          <Images className="w-4 h-4" /> Gallery
        </button>
        <button onClick={() => setPanel(panel === "saves" ? null : "saves")} data-testid="vn-saves-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white hover:bg-black/70">
          <Save className="w-4 h-4" /> Saves
        </button>
        <button onClick={restart} data-testid="vn-restart-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white hover:bg-black/70">
          <RotateCcw className="w-4 h-4" /> Restart
        </button>
      </div>

      {/* Dialogue box */}
      {!showEnding && line && (
        <div className="absolute bottom-0 left-0 right-0 z-30 p-4 sm:p-6 pointer-events-none">
          <div className="max-w-3xl mx-auto rounded-2xl bg-black/70 backdrop-blur-md border border-white/10 p-4 sm:p-5 shadow-2xl" data-testid="vn-dialogue">
            {!isNarrator && (
              <div className="inline-flex items-center gap-2 mb-2 rounded-full bg-[hsl(var(--primary))] px-3 py-1 text-sm font-bold text-white -mt-8 shadow-lg" data-testid="vn-speaker">
                <span>{speaker.emoji}</span> {speaker.name}
              </div>
            )}
            <p key={`${nodeId}-${lineIdx}-t`} className={`vn-fade text-white leading-relaxed ${isNarrator ? "italic text-white/85 text-sm sm:text-base" : "text-base sm:text-lg"}`} data-testid="vn-line-text">
              {line.text}
            </p>
            {!showChoices && (
              <div className="flex justify-end mt-2">
                <span className="inline-flex items-center gap-1 text-xs text-white/60 animate-pulse"><ChevronRight className="w-3.5 h-3.5" /> click to continue</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Choices */}
      {showChoices && (
        <div className="absolute inset-x-0 bottom-6 z-40 px-4" data-testid="vn-choices">
          <div className="max-w-lg mx-auto space-y-2.5">
            {node.choices.map((c, i) => (
              <button key={i} onClick={() => goNode(c.goto)} data-testid={`vn-choice-${i}`}
                className="w-full text-left rounded-xl bg-white/10 hover:bg-[hsl(var(--primary))] border border-white/20 backdrop-blur-md px-4 py-3 text-white font-medium transition-colors">
                {c.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ending */}
      {showEnding && (
        <div className="absolute inset-0 z-40 grid place-items-center p-6 bg-black/60 backdrop-blur-sm pointer-events-none" data-testid="vn-ending">
          <div className="text-center max-w-md pointer-events-auto">
            <p className="text-xs font-bold tracking-[0.3em] text-[hsl(var(--primary))] uppercase mb-2">Ending</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mb-4" data-testid="vn-ending-title">{node.ending_title || "The End"}</h2>
            {scene.ending_count > 1 && <p className="text-sm text-white/70 mb-6">This is 1 of {scene.ending_count} endings — replay to find the others.</p>}
            <button onClick={restart} data-testid="vn-play-again"
              className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-white font-semibold hover:opacity-90">
              <Play className="w-4 h-4" /> Play again
            </button>
          </div>
        </div>
      )}

      {/* Saves panel */}
      {panel === "saves" && (
        <div className="absolute inset-0 z-50 grid place-items-center p-6 bg-black/70 backdrop-blur-sm" data-testid="vn-saves-panel">
          <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2"><Save className="w-4 h-4" /> Save files</h3>
              <button onClick={() => setPanel(null)} data-testid="vn-saves-close" className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))]"><X className="w-4 h-4" /></button>
            </div>
            {!canSave && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Open the shared scene link while logged in to keep save files.</p>}
            {canSave && !progLoaded && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-1.5"><span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> Loading save files…</p>}
            <div className="space-y-2">
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const sl = slotAt(i);
                return (
                  <div key={i} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`vn-slot-${i}`}>
                    <span className="w-7 h-7 grid place-items-center rounded-lg bg-[hsl(var(--secondary))] text-xs font-bold shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      {sl ? <p className="text-sm truncate">{sl.label || "Saved moment"}</p> : <p className="text-sm text-[hsl(var(--muted-foreground))]">Empty slot</p>}
                    </div>
                    {sl && <button onClick={() => loadSlot(sl)} data-testid={`vn-slot-load-${i}`} className="text-xs font-semibold text-[hsl(var(--primary))] px-2 py-1">Load</button>}
                    <button onClick={() => saveSlot(i)} data-testid={`vn-slot-save-${i}`} className="text-xs font-semibold rounded-full bg-[hsl(var(--primary))] text-white px-3 py-1 hover:opacity-90">Save</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* CG Gallery panel */}
      {panel === "gallery" && (
        <div className="absolute inset-0 z-50 grid place-items-center p-6 bg-black/70 backdrop-blur-sm" data-testid="vn-gallery-panel">
          <div className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold flex items-center gap-2"><Images className="w-4 h-4" /> Scene Gallery</h3>
              <button onClick={() => setPanel(null)} data-testid="vn-gallery-close" className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{bgKeys.filter((k) => seen.includes(k)).length} of {bgKeys.length} backgrounds unlocked — discover them all as you play.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
              {bgKeys.map((k) => {
                const unlocked = seen.includes(k);
                const url = asset(bgUrls[k]);
                return (
                  <div key={k} className="aspect-video rounded-xl overflow-hidden border border-[hsl(var(--border))] relative bg-gradient-to-br from-[#1f6feb]/20 to-[#a855f7]/20" data-testid={`vn-cg-${k}`}>
                    {unlocked && url ? (
                      <img src={url} alt={k} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">
                        <Lock className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
